import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import Map from 'ol/Map';

export type MapDetails = {
  width: number | null;
  height: number | null;
  zoom: number | null;
  center: [number, number] | null;
};

interface MapViewportContextValue {
  olMapRef: React.MutableRefObject<any>;
  mapDetails: MapDetails;
  saveMapState: () => void;
}

const MapViewportContext = createContext<MapViewportContextValue | undefined>(
  undefined,
);

export const MapViewportProvider = ({ children }: { children: ReactNode }) => {
  const olMapRef = useRef<Map | null>(null);
  const [mapDetails, setMapDetails] = useState<MapDetails>({
    width: null,
    height: null,
    zoom: null,
    center: null,
  });

  const saveMapState = useCallback(() => {
    if (!olMapRef.current) {
      return;
    }

    const mapContainer = olMapRef.current.getTargetElement();
    const view = olMapRef.current.getView();

    if (!mapContainer?.clientHeight || !mapContainer?.clientWidth) {
      return;
    }

    setMapDetails({
      width: mapContainer.clientWidth,
      height: mapContainer.clientHeight,
      zoom: view.getZoom() as number,
      center: view.getCenter() as [number, number],
    });
  }, []);

  return (
    <MapViewportContext.Provider value={{ olMapRef, mapDetails, saveMapState }}>
      {children}
    </MapViewportContext.Provider>
  );
};

export const useMapViewport = () => {
  const context = useContext(MapViewportContext);
  if (!context) {
    throw new Error('useMapViewport must be used within a MapViewportProvider');
  }
  return context;
};
